import { ChangeDetectorRef, Component, NgZone, OnInit, Input, Optional, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AccountService } from 'src/app/services/account.service';
import { UserService } from 'src/app/services/user.service';
import { ToastController, AlertController, PickerController, Platform, IonInput } from '@ionic/angular';
import { Options } from './manage-network-options'
import moment from 'moment';
import { Router } from '@angular/router';
import { BarcodeScanner } from '@awesome-cordova-plugins/barcode-scanner/ngx';
import { KilnService } from 'src/app/services/kiln.service';
import { TranslateService } from '@ngx-translate/core';
import { AccountAdminKilnFlowService } from '../account-admin/account-admin-kiln-flow.service';
import { scrollFocusedFieldIntoView } from 'src/app/utils/scroll-focused-field';

@Component({
  selector: 'app-manage-network',
  templateUrl: './manage-network.component.html',
  styleUrls: ['./manage-network.component.scss'],
})
export class ManageNetworkComponent implements OnInit {
  @Input() onDismiss;
  @Input() startAddKiln = false;
  public selected_tab = 'kilns';

  public kilns = [];
  public selectedKiln = null;

  public network_devices = [];
  public selectedNetworkDevice = null;

  public manageKilnForm: UntypedFormGroup;
  public manageNetworkDeviceForm: UntypedFormGroup;

  public updating;
  public loginData;
  public hasElevatedPermissions;
  public new_kiln = false;

  /**
   * Add-flow steps when adding a new kiln: 'locate' explains where the controller serial
   * lives before 'scan' offers the scanner, then 'serial' | 'name' | 'details'.
   */
  public addModeStep: 'locate' | 'scan' | 'serial' | 'name' | 'details' | null = null;
  public addFlowSerial = '';
  public addFlowError = '';
  public submittingNewKiln = false;
  public checkingAddFlowSerial = false;

  private accountInformation;
  public userAccounts: Array<{ id: number; name: string }> = [];
  /** Prevents launching two native scanner instances (common iOS WKWebView issue). */
  private scanInProgress = false;
  /** undefined until account users load; do not treat undefined as read-only. */
  public canAdminister: boolean | undefined;
  public retrievingInformation;

  /** Editable kiln fields unless explicitly denied (undefined = still loading, allow edit). */
  get canEditKilnFields(): boolean {
    return this.canAdminister !== false;
  }

  public model_options = Options.all;
  public phase_options = ["Single Phase", "Two Phase", "Three Phase", "Other"];
  public voltage_options = ["115V", "208V", "240V", "Other"];
  @Input() small_screen = false;

  @ViewChild('kilnNameInput', { static: false }) kilnNameInput?: IonInput;

  /** No kilns on the account and not in the add-kiln flow (full-width empty / loading; not the edit form). */
  get kilnTabEmptyLayout(): boolean {
    return (!this.kilns || this.kilns.length === 0) && !this.new_kiln;
  }

  /** The kiln detail form replaces the add-flow steps only once the flow reaches 'details'. */
  get showKilnDetailsForm(): boolean {
    const inEarlyAddStep = this.new_kiln && !!this.addModeStep && this.addModeStep !== 'details';
    return (!!this.selectedKiln || this.new_kiln) && !inEarlyAddStep;
  }

  /** Hide bottom Cancel/Submit when the kiln list is empty (nothing to save). */
  get showKilnTabFooter(): boolean {
    if (this.selected_tab !== 'kilns') {
      return true;
    }
    return !this.kilnTabEmptyLayout;
  }

  /** True when rendered inside Account Admin (tab bar handles navigation). */
  get embeddedInAccountAdmin(): boolean {
    return (this.router.url || '').includes('account-admin');
  }

  get currentAccountName(): string {
    const name = this.accountInformation?.general?.name;
    if (name) {
      return name;
    }
    const currentId = this.accountService.getCurrentAccount();
    const found = this.userAccounts.find((acct) => acct.id === currentId);
    return found?.name || '';
  }

  /** User belongs to more than one account context (paid accounts and/or KilnLink Free). */
  get hasMultipleAccounts(): boolean {
    const accountCount = this.userAccounts.length;
    return accountCount > 1 || (accountCount >= 1 && this.userService.hasKilnlinkFreeKilns());
  }

  get canSwitchAccounts(): boolean {
    return this.hasMultipleAccounts && !this.userService.isLoggedInAsAnotherUser();
  }

  /** Only the account owner can transfer a kiln. Login-as cannot. */
  get isCurrentUserAccountOwner(): boolean {
    if (this.userService.isLoggedInAsAnotherUser()) {
      return false;
    }
    const users = this.accountInformation?.users || [];
    const me = users.find((user) => {
      const userEmail = (user.email || '').trim().toLowerCase();
      const loginEmail = (this.loginData?.email || '').trim().toLowerCase();
      if (userEmail && loginEmail && userEmail === loginEmail) {
        return true;
      }
      return user.username && this.loginData?.username && user.username === this.loginData.username;
    });
    if (me?.is_owner) {
      return true;
    }
    const owner = this.accountInformation?.account_owner;
    const userIdentity = (this.loginData?.email || this.loginData?.username || '').trim().toLowerCase();
    const ownerIdentity = (owner?.email || owner?.username || '').trim().toLowerCase();
    return !!userIdentity && !!ownerIdentity && userIdentity === ownerIdentity;
  }

  get canTransferKiln(): boolean {
    return this.isCurrentUserAccountOwner && !!this.selectedKiln && !this.new_kiln;
  }

  kilnTransferConfirmMessage(email: string): string {
    return this.translate.instant('ACCOUNT_ADMIN.TRANSFER_KILN_CONFIRM', { email });
  }

  scrollFormFieldIntoView(event: Event): void {
    scrollFocusedFieldIntoView(event as FocusEvent, { enabled: this.small_screen });
  }

  constructor(public accountService: AccountService,
    public userService: UserService,
    public toastCtrl: ToastController,
    private alertController: AlertController,
    private pickerController: PickerController,
    private platform: Platform,
    private router: Router,
    private barcodeScanner: BarcodeScanner,
    private kilnService: KilnService,
    private translate: TranslateService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    @Optional() private accountAdminKilnFlow?: AccountAdminKilnFlowService
  ) {
      let login_data = this.userService.getLoginData();
      this.loginData = (login_data.hasOwnProperty('loggedInAs') && login_data.loggedInAs) ? login_data.loggedInAs : login_data;
      this.retrievingInformation = true;

      this.hasElevatedPermissions = this.userService.hasElevatedPermissions();
      this.accountService.getAccountInformation().then((response) => {
        this.accountInformation = response;
        this.setUpCanAdminister(response);

        let current_user_permissions_for_account = response.users ? response.users.find((u) => { return u.email === this.loginData.email }) : {type: 'basic'}
        this.hasElevatedPermissions = current_user_permissions_for_account && current_user_permissions_for_account.type && current_user_permissions_for_account.type === 'admin';
      });
      this.accountService.getAllAccounts()
        .then((accounts) => {
          this.userAccounts = accounts || [];
        })
        .catch(() => {
          this.userAccounts = [];
        });
      this.setSmallScreen();
  }

  goToSwitchAccounts(): void {
    this.router.navigate(['/switch-account']);
  }

  ngOnInit() {
    this.getAccountDevices();
    this.hasElevatedPermissions = this.userService.hasElevatedPermissions();
    this.updating = false;
    this.new_kiln = false;
    this.addModeStep = null;
    this.addFlowSerial = '';

    this.setSmallScreen();
    if (this.startAddKiln) {
      this.startAddFlow();
    }
  }

  setSmallScreen() {
    let is_device = this.platform ? (this.platform.is('mobile') || this.platform.is('tablet') || this.platform.is('phablet') || this.platform.is('mobileweb')) : false;
    this.small_screen = this.platform.width() < 875 || is_device;
  }

  private getAccountDevices(): Promise<void> {
    return this.accountService.getDevicesForAccount()
        .then((data) => {
          this.kilns = (data.kilns || []).sort((a, b) => (a.name > b.name) ? 1 : -1);
          this.network_devices = (data.network_devices || []).filter((nd) => nd.id !== '-1').sort((a, b) => (a.name > b.name) ? 1 : -1);
          if (this.kilns.length > 0 && !this.new_kiln) {
            this.selectKiln(this.kilns[0]);
          } else if (this.kilns.length === 0 && !this.new_kiln) {
            this.selectedKiln = null;
          }
          if (this.network_devices.length > 0) {
            this.selecteNetworkDevice(this.network_devices[0]);
          }
          this.retrievingInformation = false;
        }).catch(() => { this.retrievingInformation = false; });
  }

  /** Show controller serial when KMT is selected or when editing a kiln that already has one. */
  get showControllerSerialField(): boolean {
    if (!this.manageKilnForm) {
      return false;
    }
    if (this.manageKilnForm.get('is_kmt')?.value) {
      return true;
    }
    if (!this.new_kiln) {
      const serial = this.normalizeControllerSerial(this.manageKilnForm.get('serial_number')?.value || '');
      return this.isControllerSerialValid(serial);
    }
    return false;
  }

  selectKiln(kiln) {
    this.new_kiln = false;
    this.addModeStep = null;
    this.addFlowSerial = '';
    this.selectedKiln = kiln;
    let name = new UntypedFormControl(kiln ? kiln.name : 'Please enter a kiln name', [Validators.required]);
    let model = new UntypedFormControl(kiln?.model ?? '', []);
    let voltage = new UntypedFormControl(kiln?.voltage ?? '', []);
    let phase = new UntypedFormControl(kiln?.phase ?? '', []);
    let serial_number = new UntypedFormControl(this.getControllerSerialFromKiln(kiln), []);
    let kiln_id = new UntypedFormControl(kiln ? kiln.kiln_id : 'Kiln ID', [Validators.min(1)]);
    let install_date = new UntypedFormControl((kiln && kiln.install_date) ? moment(kiln.install_date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"), []);
    let nd = new UntypedFormControl(kiln && (kiln.network_device_id), []);
    let kiln_serial_number = new UntypedFormControl(kiln && (kiln.kiln_serial_number), []);

    this.manageKilnForm = new UntypedFormGroup({
      name: name,
      model: model,
      voltage: voltage,
      phase: phase,
      serial_number: serial_number,
      kiln_id: kiln_id,
      install_date: install_date,
      network_device: nd,
      is_kmt: new UntypedFormControl(kiln ? (kiln.is_kmt || false) : false, [Validators.required]),
      mac_address: new UntypedFormControl(kiln ? this.normalizeMacAddress(kiln.mac_address || '') : '', []),
      external_id: new UntypedFormControl(kiln ? kiln.id : null),
      kiln_serial_number: kiln_serial_number
    });
  }

  selecteNetworkDevice(network_device) {
    this.new_kiln = false;
    this.selectedNetworkDevice = network_device;
    let name = new UntypedFormControl(network_device ? network_device.name : 'Please enter a device name', [Validators.required]);
    let mac_address = new UntypedFormControl(network_device ? network_device.mac_address : 'Mac Address', [Validators.required]);
    let model = new UntypedFormControl(network_device ? network_device.model : 'Model', [Validators.required]);
    let serial_number = new UntypedFormControl(network_device ? network_device.serial_number : 'Serial Number', [Validators.required]);
    let device_manufacturer = new UntypedFormControl(network_device ? network_device.device_manufacturer : 'Device Manufacturer', [Validators.required]);

    this.manageNetworkDeviceForm = new UntypedFormGroup({
      name: name,
      mac_address: mac_address,
      model: model,
      serial_number: serial_number,
      device_manufacturer: device_manufacturer
    });
  }

  submitKiln(formValue) {
    this.addFlowError = '';
    if (formValue?.is_kmt) {
      const serialError = this.getControllerSerialValidationError(formValue.serial_number || '');
      if (serialError) {
        this.addFlowError = serialError;
        return;
      }
      const macError = this.getMacAddressValidationError(formValue.mac_address || '');
      if (macError) {
        this.addFlowError = macError;
        return;
      }
      formValue = { ...formValue, serial_number: this.normalizeControllerSerial(formValue.serial_number) };
    } else {
      formValue = { ...formValue };
      delete formValue.serial_number;
      delete formValue.mac_address;
    }

    formValue = this.prepareKilnFormValue(formValue);
    const payload: Record<string, unknown> = { type: 'kiln', ...formValue };
    if (payload.is_kmt) {
      delete payload.network_device;
    }

    this.updating = true;
    const kilnId = this.new_kiln ? undefined : formValue.external_id;
    delete payload.external_id;

    this.accountService.upsertKilnForAccount(kilnId, payload)
        .then(() => {
          this.userService.setCurrentAccount(this.accountInformation.general.external_id);
          this.upsertSuccess(this.translate.instant(this.new_kiln ? 'MANAGE_NETWORK.KILN_WAS_CREATED' : 'MANAGE_NETWORK.KILN_WAS_UPDATED'));
        })
        .catch(() => {
          this.upsertFailed(this.translate.instant(this.new_kiln ? 'MANAGE_NETWORK.COULD_NOT_CREATE_KILN' : 'MANAGE_NETWORK.COULD_NOT_UPDATE_KILN'));
        });
  }

  submitNetworkDevice(formValue) {
    this.updating = true;
    this.accountService.upsertNetworkDeviceForAccount(formValue.serial_number, {...{type: "network_device"}, ...formValue})
        .then((response) => {
          this.userService.setCurrentAccount(this.accountInformation.general.external_id);
          this.upsertSuccess(this.translate.instant(this.new_kiln ? 'MANAGE_NETWORK.NETWORK_DEVICE_WAS_CREATED' : 'MANAGE_NETWORK.NETWORK_DEVICE_WAS_UPDATED'));
        })
        .catch((error) => {
          this.upsertFailed(this.translate.instant(this.new_kiln ? 'MANAGE_NETWORK.COULD_NOT_CREATE_NETWORK_DEVICE' : 'MANAGE_NETWORK.COULD_NOT_UPDATE_NETWORK_DEVICE'));
        })
  }

  submit() {
    if (this.new_kiln && this.addModeStep === 'details') {
      this.submitNewKiln();
    } else if (!this.new_kiln) {
      this.submitKiln(this.manageKilnForm.value);
    }
  }

  async upsertSuccess(message) {
    this.new_kiln = false;
    this.addModeStep = null;
    this.addFlowSerial = '';
    this.addFlowError = '';
    this.getAccountDevices();
    this.updating = false;
    this.submittingNewKiln = false;
    this.showMessage(message || this.translate.instant('MANAGE_NETWORK.UPDATE_SUCCESSFUL'));
  }

  async upsertFailed(error) {
    this.updating = false;
    const message = this.getUserFriendlyErrorMessage(error, this.translate.instant('MANAGE_NETWORK.UPDATE_FAILED'));
    this.showMessage(message);
  }

  /** Returns a user-friendly message; hides raw HTTP/500 errors */
  private getUserFriendlyErrorMessage(err: any, fallback?: string): string {
    const msg = err?.error?.message || err?.message || (typeof err === 'string' ? err : '');
    const isTechnicalError = typeof msg === 'string' && (
      msg.includes('Http failure') ||
      msg.includes('500') ||
      msg.includes('Internal Server Error') ||
      msg.includes('InternalServerError')
    );
    if (isTechnicalError) {
      return this.translate.instant('MANAGE_NETWORK.SORRY_SOMETHING_WENT_WRONG');
    }
    return msg || fallback || '';
  }

  private getAddKilnErrorBody(err: any): Record<string, unknown> {
    if (!err) {
      return {};
    }
    return (err.error ?? err.body ?? err) as Record<string, unknown>;
  }

  private formatValidationMessages(msgs: string[]): string {
    return (msgs || []).map((msg) => {
      let formatted = String(msg).replace(/"/g, '');
      formatted = formatted.replace(/_/g, ' ');
      return `${formatted.charAt(0).toUpperCase()}${formatted.slice(1)}.`;
    }).join(' ');
  }

  parseAddKilnError(err: any): string {
    const status = err?.status ?? err?.statusCode;
    const body = this.getAddKilnErrorBody(err);
    const message = typeof body?.message === 'string'
      ? body.message
      : (typeof err?.message === 'string' ? err.message : '');
    const msgs = Array.isArray(body?.msgs) ? body.msgs as string[] : [];
    const messageLower = message.toLowerCase();

    if (status === 409 || messageLower.includes('already claimed')) {
      return this.translate.instant('ADD_KILN.ALREADY_CLAIMED');
    }

    if (status === 404) {
      return this.translate.instant('ADD_KILN.UNKNOWN_SERIAL');
    }

    if (message.includes('Serial number must be 8 or 9 digits')) {
      return this.translate.instant('MANAGE_NETWORK.CONTROLLER_SERIAL_MUST_BE_8_OR_9_DIGITS');
    }
    if (message.includes('Invalid or unknown kiln serial number')) {
      return this.translate.instant('ADD_KILN.UNKNOWN_SERIAL');
    }

    if (status === 400 || msgs.length > 0) {
      if (msgs.length > 0) {
        return `${this.translate.instant('ADD_KILN.CHECK_FIELDS')} ${this.formatValidationMessages(msgs)}`.trim();
      }
      if (message === 'Invalid claim payload') {
        return this.translate.instant('ADD_KILN.CHECK_FIELDS');
      }
      if (message) {
        return message;
      }
    }

    if (status === 401) {
      if (messageLower.includes('already claimed')) {
        return this.translate.instant('ADD_KILN.ALREADY_CLAIMED');
      }
      return this.translate.instant('ADD_KILN.NO_PERMISSION');
    }

    const friendly = this.getUserFriendlyErrorMessage(err, '');
    if (friendly) {
      return friendly;
    }
    return this.translate.instant('ADD_KILN.COULD_NOT_ADD_KILN');
  }

  async showMessage(message) {
    let toast = await this.toastCtrl.create({
      message: message,
      duration: 3000,
      position: 'middle',
      cssClass: 'success'
    });

    await toast.present();
  }

  addKiln() {
    this.startAddFlow();
  }

  /** Start add-kiln flow: explain where to find the controller serial, then scan, serial or details */
  startAddFlow() {
    this.addFlowError = '';
    this.addFlowSerial = '';
    this.new_kiln = true;
    this.addModeStep = 'locate';
    this.selected_tab = 'kilns';
    this.buildFormForNewKiln('');
  }

  goToScanStep() {
    this.addModeStep = 'scan';
    this.addFlowError = '';
  }

  goToSerialStep() {
    this.addModeStep = 'serial';
    this.addFlowError = '';
  }

  /** Move from scan/serial to details form, or claim immediately if kiln is already claimed. */
  goToDetailsStepFromAdd(): Promise<void> {
    this.addFlowError = '';
    this.addFlowSerial = this.normalizeControllerSerial(this.addFlowSerial);
    const serialError = this.getControllerSerialValidationError(this.addFlowSerial);
    if (serialError) {
      this.addFlowError = serialError;
      return Promise.resolve();
    }
    return this.checkClaimStatusAndProceed();
  }

  private getAccountIdForApi(): number | undefined {
    return this.accountInformation?.general?.external_id ?? this.accountInformation?.id;
  }

  private isSerialAlreadyOnAccount(serial: string): boolean {
    const normalized = this.normalizeControllerSerial(serial);
    return (this.kilns || []).some((kiln) => {
      const kilnSerial = this.normalizeControllerSerial(String(kiln?.serial_number ?? ''));
      return kilnSerial === normalized;
    });
  }

  private async checkClaimStatusAndProceed() {
    this.checkingAddFlowSerial = true;
    this.addFlowError = '';
    this.cdr.detectChanges();

    const accountId = this.getAccountIdForApi();

    if (this.isSerialAlreadyOnAccount(this.addFlowSerial)) {
      this.addFlowError = this.translate.instant('MANAGE_NETWORK.SERIAL_ALREADY_ON_ACCOUNT');
      this.checkingAddFlowSerial = false;
      this.cdr.detectChanges();
      return;
    }

    try {
      const status = await this.accountService.lookupKilnSerialForAccount(this.addFlowSerial, accountId);
      if (status?.canName === false) {
        const confirmed = await this.confirmLinkToExistingFreeAccount(this.addFlowSerial);
        if (confirmed) {
          await this.claimExistingKilnBySerial(this.addFlowSerial);
        }
        return;
      }
      this.showNameStepForNewKiln();
    } catch (err) {
      this.addFlowError = this.parseAddKilnError(err);
      this.cdr.detectChanges();
    } finally {
      this.checkingAddFlowSerial = false;
      this.cdr.detectChanges();
    }
  }

  private showNameStepForNewKiln() {
    this.addModeStep = 'name';
    this.buildFormForNewKiln(this.addFlowSerial || '');
    this.cdr.detectChanges();
    this.focusKilnName();
  }

  goToFullDetailsFromName() {
    this.addFlowError = '';
    const name = (this.manageKilnForm?.get('name')?.value || '').trim();
    if (!name) {
      this.addFlowError = this.translate.instant('MANAGE_NETWORK.COMPLETE_REQUIRED_FIELDS');
      return;
    }
    this.addModeStep = 'details';
    this.cdr.detectChanges();
  }

  canProceedFromNameStep(): boolean {
    if (!this.manageKilnForm) {
      return false;
    }
    return !!(this.manageKilnForm.get('name')?.value || '').trim();
  }

  private async confirmLinkToExistingFreeAccount(serial: string): Promise<boolean> {
    const alert = await this.alertController.create({
      header: this.translate.instant('MANAGE_NETWORK.FREE_ACCOUNT_KILN_CLAIM_TITLE'),
      message: this.translate.instant('MANAGE_NETWORK.FREE_ACCOUNT_KILN_CLAIM_MESSAGE', { serial }),
      buttons: [
        {
          text: this.translate.instant('MANAGE_NETWORK.NO_CANCEL'),
          role: 'cancel',
        },
        {
          text: this.translate.instant('ADD_KILN.LINK'),
          role: 'confirm',
        },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    return role === 'confirm';
  }

  private async claimExistingKilnBySerial(serial: string): Promise<void> {
    const currentAccountId = this.getAccountIdForApi();
    const hadNoKilnsBefore = !this.kilns?.length;
    this.submittingNewKiln = true;
    this.addFlowError = '';

    try {
      const claimResponse = await this.kilnService.addKiln(
        serial,
        serial,
        null,
        { is_kmt: true, serial_number: serial },
        currentAccountId
      );
      await this.finalizeAfterKilnClaim(claimResponse, serial, hadNoKilnsBefore);
    } catch (err) {
      this.submittingNewKiln = false;
      const message = this.parseAddKilnError(err);
      this.addFlowError = message;
      this.showMessage(message);
      this.cdr.detectChanges();
    }
  }

  private isLinkedToOtherAccountClaim(
    claimResponse: { account_id?: number; linked_to_existing_account?: boolean },
    requestingAccountId?: number,
  ): boolean {
    const kilnAccountId = claimResponse?.account_id != null ? Number(claimResponse.account_id) : null;
    if (claimResponse?.linked_to_existing_account === true) {
      return kilnAccountId != null;
    }
    return kilnAccountId != null && requestingAccountId != null && kilnAccountId !== requestingAccountId;
  }

  private async finalizeAfterKilnClaim(
    claimResponse: { account_id?: number; serial_number?: string; linked_to_existing_account?: boolean; linked_account_name?: string },
    serial: string,
    hadNoKilnsBefore: boolean,
  ): Promise<void> {
    const requestingAccountId = this.getAccountIdForApi();
    const kilnAccountId = claimResponse?.account_id != null ? Number(claimResponse.account_id) : null;
    const linkedToOtherAccount = this.isLinkedToOtherAccountClaim(claimResponse, requestingAccountId);

    if (linkedToOtherAccount && kilnAccountId != null) {
      this.accountService.setCurrent(kilnAccountId);
      await this.userService.setCurrentAccount(kilnAccountId);
      await this.userService.updateLoginData();
      await this.userService.loadAllKilnInfo();
      const refreshed = await this.accountService.getAccountInformation();
      this.accountInformation = refreshed;
      this.setUpCanAdminister(refreshed);
    } else {
      const stayOnAccountId = this.accountInformation?.general?.external_id ?? requestingAccountId;
      if (stayOnAccountId != null) {
        await this.userService.setCurrentAccount(stayOnAccountId);
      }
      await this.userService.updateLoginData();
    }

    this.new_kiln = false;
    this.addModeStep = null;
    this.addFlowSerial = '';
    this.addFlowError = '';
    this.submittingNewKiln = false;
    this.updating = false;

    await this.getAccountDevices();

    const normalizedSerial = this.normalizeControllerSerial(serial || claimResponse?.serial_number || '');
    const matchedKiln = (this.kilns || []).find((kiln) =>
      this.normalizeControllerSerial(String(kiln?.serial_number ?? '')) === normalizedSerial
    );
    if (matchedKiln) {
      this.selectKiln(matchedKiln);
    }

    const accountName =
      claimResponse?.linked_account_name ||
      this.accountInformation?.general?.name ||
      this.accountInformation?.name ||
      '';
    if (linkedToOtherAccount) {
      const message = accountName
        ? this.translate.instant('MANAGE_NETWORK.KILN_LINKED_ON_OTHER_ACCOUNT', { accountName })
        : this.translate.instant('MANAGE_NETWORK.KILN_LINKED_ON_OTHER_ACCOUNT_NO_NAME');
      await this.presentKilnLinkedSuccessAlert(message);
    } else {
      const message = `${this.translate.instant('MANAGE_NETWORK.KILN_LINKED_CAN_VIEW_STATUS')} ${this.kilnPermissionsFollowUp()}`;
      // The first kiln hands off to the walkthrough's next-step dialog, which offers its own
      // Set Up Permissions option, so a second action here would compete with it.
      await this.presentKilnLinkedSuccessAlert(message, {
        offerPermissionsSetup: !hadNoKilnsBefore && this.canAdminister !== false,
      });
    }
    this.cdr.detectChanges();

    if (hadNoKilnsBefore) {
      this.accountAdminKilnFlow?.notifyKilnAddedFromWalkthroughSetup();
    }
  }

  /** What the admin can configure for a kiln on their own account; premium adds alerts and programming. */
  private kilnPermissionsFollowUp(): string {
    return this.userService.isPremium()
      ? this.translate.instant('MANAGE_NETWORK.KILN_LINKED_PERMISSIONS_PREMIUM')
      : this.translate.instant('MANAGE_NETWORK.KILN_LINKED_PERMISSIONS');
  }

  private async presentKilnLinkedSuccessAlert(
    message: string,
    options: { offerPermissionsSetup?: boolean } = {},
  ): Promise<void> {
    let goToPermissions = false;
    const buttons: Array<string | { text: string; handler: () => void }> = [
      this.translate.instant('MANAGE_NETWORK.KILN_LINKED_SUCCESS_OK'),
    ];

    if (options.offerPermissionsSetup) {
      buttons.push({
        text: this.translate.instant('MANAGE_NETWORK.KILN_LINKED_PERMISSIONS_ACTION'),
        handler: () => {
          goToPermissions = true;
        },
      });
    }

    const alert = await this.alertController.create({
      header: this.translate.instant('MANAGE_NETWORK.KILN_LINKED_SUCCESS_TITLE'),
      message,
      buttons,
    });
    await alert.present();
    await alert.onDidDismiss();

    if (goToPermissions) {
      this.goToKilnUserPermissions();
    }
  }

  /** Account Admin users tab, where per-kiln view/stop (and premium alert/program) access is granted. */
  private goToKilnUserPermissions(): void {
    void this.router.navigate(['/account-admin'], {
      queryParams: { tab: 'users', add: null },
      queryParamsHandling: 'merge',
    });
  }

  /** Skip scan/serial and go straight to details form */
  skipToDetailsFromAdd() {
    this.addFlowSerial = '';
    this.addFlowError = '';
    this.addModeStep = 'details';
    this.buildFormForNewKiln('');
    this.cdr.detectChanges();
    this.focusKilnName();
  }

  cancelAddFlow() {
    this.addModeStep = null;
    this.addFlowSerial = '';
    this.addFlowError = '';
    this.new_kiln = false;
    if (this.kilns && this.kilns.length > 0) {
      this.selectKiln(this.kilns[0]);
    } else {
      this.selectedKiln = null;
    }
  }

  private buildFormForNewKiln(serial: string) {
    this.manageKilnForm = new UntypedFormGroup({
      name: new UntypedFormControl("", [Validators.required]),
      model: new UntypedFormControl("", []),
      voltage: new UntypedFormControl("", []),
      phase: new UntypedFormControl("", []),
      serial_number: new UntypedFormControl(serial, []),
      kiln_id: new UntypedFormControl("", [Validators.min(1)]),
      install_date: new UntypedFormControl(moment().format("YYYY-MM-DD"), []),
      network_device: new UntypedFormControl("", []),
      is_kmt: new UntypedFormControl(true, [Validators.required]),
      mac_address: new UntypedFormControl('', []),
      kiln_serial_number: new UntypedFormControl("", []),
      external_id: new UntypedFormControl(null, [])
    });
  }

  private focusKilnName() {
    // ViewChild is unavailable until after the details form renders (e.g. post-scan step change).
    // Delay focus until the native barcode overlay is gone so iOS will show the keyboard.
    const focusDelayMs = this.platform.is('ios') || this.platform.is('ipad') ? 450 : 250;
    setTimeout(async () => {
      const input = this.kilnNameInput;
      if (!input) {
        return;
      }
      try {
        await input.setFocus();
        const el = await input.getInputElement();
        if (el?.value) {
          el.select();
        }
      } catch (_) {
        /* ignore focus errors on unsupported platforms */
      }
    }, focusDelayMs);
  }

  scanCode() {
    this.addFlowError = '';
    if (!this.platform.is('cordova')) {
      this.addFlowError = this.translate.instant('MANAGE_NETWORK.SCAN_REQUIRES_NATIVE_APP');
      return;
    }
    if (this.scanInProgress) {
      return;
    }
    this.scanInProgress = true;
    this.cdr.detectChanges();

    const scanOptions = this.getBarcodeScanOptions();
    const finishScan = () => {
      this.scanInProgress = false;
      this.cdr.detectChanges();
    };

    const runScan = () => this.barcodeScanner.scan(scanOptions).then((barcodeData) => {
      this.ngZone.run(() => this.checkScanData(barcodeData));
    }, (err) => {
      this.ngZone.run(() => {
        this.addFlowError = this.getUserFriendlyErrorMessage(err, this.translate.instant('MANAGE_NETWORK.SCAN_FAILED_TRY_MANUAL'));
        this.cdr.detectChanges();
      });
    }).finally(() => {
      this.ngZone.run(finishScan);
    });

    void this.platform.ready().then(runScan).catch((err) => {
      this.addFlowError = this.getUserFriendlyErrorMessage(err, this.translate.instant('MANAGE_NETWORK.SCAN_FAILED_TRY_MANUAL'));
      finishScan();
    });
  }

  /** iOS: minimal options — extra settings often break the legacy barcode plugin. Android: full options. */
  private getBarcodeScanOptions(): Record<string, unknown> {
    if (this.platform.is('ios') || this.platform.is('ipad')) {
      return {
        prompt: 'Center the barcode inside the scan area',
      };
    }

    return {
      preferFrontCamera: false,
      showFlipCameraButton: false,
      showTorchButton: true,
      torchOn: false,
      prompt: 'Center the barcode inside the scan area',
      resultDisplayDuration: 500,
      disableAnimations: true,
      disableSuccessBeep: false,
      formats: 'QR_CODE,CODE_39,CODE_128,UPC_A,UPC_E,EAN_13,EAN_8',
    };
  }

  checkScanData(data: { cancelled?: boolean; text?: string; format?: string }) {
    if (!data || data.cancelled === true) {
      // User closed the scanner without scanning.
      this.cdr.detectChanges();
      return;
    }

    const rawText = String(data.text ?? '').trim();
    if (!rawText) {
      this.setScanFeedback(this.translate.instant('MANAGE_NETWORK.SCAN_FAILED_TRY_MANUAL'));
      return;
    }

    this.addFlowSerial = this.normalizeControllerSerial(rawText);
    const serialError = this.getControllerSerialValidationError(this.addFlowSerial);
    if (serialError) {
      this.setScanFeedback(serialError);
      return;
    }

    const focusDelayMs = this.platform.is('ios') || this.platform.is('ipad') ? 150 : 0;
    // Brief pause after scanner dismisses before advancing the add flow (iOS focus/keyboard).
    setTimeout(() => {
      this.ngZone.run(() => void this.goToDetailsStepFromAdd());
    }, focusDelayMs);
    this.cdr.detectChanges();
  }

  private setScanFeedback(message: string): void {
    this.addFlowError = message;
    this.cdr.detectChanges();
  }

  /** Clear optional kiln fields; omit legacy placeholder defaults from the form. */
  private prepareKilnFormValue(formValue: Record<string, unknown>): Record<string, unknown> {
    const out = { ...formValue };
    out.model = this.emptyOptionalKilnField(out.model, ['Model']);
    out.voltage = this.emptyOptionalKilnField(out.voltage, ['Voltage']);
    out.phase = this.emptyOptionalKilnField(out.phase, ['Phase']);
    out.kiln_serial_number = this.emptyOptionalKilnField(out.kiln_serial_number, []);
    if (out.is_kmt) {
      out.serial_number = this.emptyOptionalKilnField(out.serial_number, ['Serial No.']);
      out.mac_address = this.normalizeOptionalMacAddress(out.mac_address);
    }
    return out;
  }

  private getControllerSerialFromKiln(kiln?: { serial_number?: string | number | null }): string {
    const serial = this.normalizeControllerSerial(String(kiln?.serial_number ?? ''));
    return this.isControllerSerialValid(serial) ? serial : '';
  }

  private normalizeOptionalMacAddress(value: unknown): string | null {
    const normalized = this.normalizeMacAddress(String(value ?? ''));
    return normalized || null;
  }

  normalizeMacAddress(raw: string): string {
    return String(raw || '').trim().toUpperCase().replace(/[:-]/g, '');
  }

  isMacAddressValid(mac: string): boolean {
    const normalized = this.normalizeMacAddress(mac);
    if (!normalized) {
      return true;
    }
    return /^[0-9A-F]{12}$/.test(normalized);
  }

  private getMacAddressValidationError(mac: string): string | null {
    if (this.isMacAddressValid(mac)) {
      return null;
    }
    return this.translate.instant('MANAGE_NETWORK.MAC_ADDRESS_INVALID');
  }

  private emptyOptionalKilnField(
    value: unknown,
    legacyPlaceholders: string[]
  ): string | null {
    const trimmed = String(value ?? '').trim();
    if (!trimmed || legacyPlaceholders.includes(trimmed)) {
      return null;
    }
    return trimmed;
  }

  /** Matches claim API: exactly 8 or 9 decimal digits. */
  private normalizeControllerSerial(raw: string): string {
    return String(raw || '').trim();
  }

  isControllerSerialValid(serial: string): boolean {
    const s = this.normalizeControllerSerial(serial);
    return /^\d{8}$|^\d{9}$/.test(s);
  }

  private getControllerSerialValidationError(serial: string): string | null {
    const normalized = this.normalizeControllerSerial(serial);
    if (!normalized) {
      return this.translate.instant('MANAGE_NETWORK.SERIAL_EMPTY_TRY_AGAIN');
    }
    if (!this.isControllerSerialValid(normalized)) {
      return this.translate.instant('MANAGE_NETWORK.CONTROLLER_SERIAL_MUST_BE_8_OR_9_DIGITS');
    }
    return null;
  }

  private getKmtControllerSerialFromForm(): string {
    return this.normalizeControllerSerial(
      (this.addFlowSerial || '') || (this.manageKilnForm?.get('serial_number')?.value || '')
    );
  }

  /** True when name and controller serial (if touchscreen) are present so Create Kiln can be enabled */
  canSubmitNewKiln(): boolean {
    if (!this.manageKilnForm) return false;
    const name = (this.manageKilnForm.get('name')?.value || '').trim();
    if (!name) return false;
    const isKmt = this.manageKilnForm.get('is_kmt')?.value;
    if (isKmt) {
      if (!this.getKmtControllerSerialFromForm()) return false;
      if (!this.isControllerSerialValid(this.getKmtControllerSerialFromForm())) return false;
    }
    return true;
  }

  submitNewKiln() {
    if (!this.manageKilnForm || !this.manageKilnForm.valid) {
      this.addFlowError = this.translate.instant('MANAGE_NETWORK.COMPLETE_REQUIRED_FIELDS');
      return;
    }
    const formValue = this.prepareKilnFormValue({ ...this.manageKilnForm.value, type: 'kiln' });
    const name = String(formValue.name ?? '');
    if (formValue.is_kmt) {
      const serialError = this.getControllerSerialValidationError(this.getKmtControllerSerialFromForm());
      if (serialError) {
        this.addFlowError = serialError;
        return;
      }
      const macError = this.getMacAddressValidationError(String(formValue.mac_address ?? ''));
      if (macError) {
        this.addFlowError = macError;
        return;
      }
      const normalizedSerial = this.getKmtControllerSerialFromForm();
      formValue.serial_number = normalizedSerial;
      this.addFlowSerial = normalizedSerial;
    }
    const serial = formValue.is_kmt && this.addFlowSerial ? this.addFlowSerial : null;
    const currentAccountId = this.getAccountIdForApi();
    this.submittingNewKiln = true;
    this.addFlowError = '';
    const hadNoKilnsBefore = !this.kilns?.length;

    const doUpsert = (externalId?: string, omitName = false) => {
      const id = externalId || serial;
      const upsertPayload: any = { ...formValue };
      if(upsertPayload.is_kmt) { delete upsertPayload.network_device; }
      delete upsertPayload.external_id;
      if (omitName) delete upsertPayload.name;

      return this.accountService.upsertKilnForAccount(id, upsertPayload);
    };

    const onUpsertSuccess = () => {
      this.userService.setCurrentAccount(this.accountInformation?.general?.external_id);
      this.upsertSuccess(this.translate.instant('MANAGE_NETWORK.KILN_SUCCESSFULLY_ADDED'));
      this.userService.updateLoginData();
      if (hadNoKilnsBefore) {
        this.accountAdminKilnFlow?.notifyKilnAddedFromWalkthroughSetup();
      }
    };

    if (serial) {
      const claimPayload = { ...formValue };
      delete claimPayload.external_id;
      delete claimPayload.type;
      delete claimPayload.network_device;
      this.kilnService.addKiln(serial, name, null, claimPayload, currentAccountId)
        .then((claimResponse) => this.finalizeAfterKilnClaim(claimResponse, serial, hadNoKilnsBefore))
        .catch((err) => {
          this.submittingNewKiln = false;
          this.addFlowError = this.parseAddKilnError(err);
        });
    } else {
      doUpsert()
        .then(() => onUpsertSuccess())
        .catch((err) => {
          this.submittingNewKiln = false;
          this.addFlowError = this.parseAddKilnError(err);
        });
    }
  }

  async transferKiln() {
    if (!this.canTransferKiln) {
      return;
    }
    const email = await this.promptForTransferEmail();
    if (!email) {
      return;
    }
    const confirmed = await this.confirmTransfer(
      'ACCOUNT_ADMIN.TRANSFER_KILN_CONFIRM_HEADER',
      this.kilnTransferConfirmMessage(email)
    );
    if (!confirmed) {
      return;
    }
    const accountId = this.accountInformation?.general?.external_id ?? this.accountInformation?.id;
    this.accountService.transferKiln(accountId, this.selectedKiln.id, email)
      .then(() => {
        const externalId = this.accountInformation?.general?.external_id;
        if (externalId != null) {
          this.userService.setCurrentAccount(externalId);
        }
        this.showMessage(this.translate.instant('ACCOUNT_ADMIN.TRANSFER_KILN_SUCCESS'));
        this.getAccountDevices();
      })
      .catch((err) => {
        this.showMessage(this.getUserFriendlyErrorMessage(err, this.translate.instant('ACCOUNT_ADMIN.TRANSFER_KILN_FAILURE')));
      });
  }

  private promptForTransferEmail(): Promise<string | null> {
    return new Promise(async (resolve) => {
      let settled = false;
      const finish = (value: string | null) => {
        if (!settled) {
          settled = true;
          resolve(value);
        }
      };
      const alert = await this.alertController.create({
        header: this.translate.instant('ACCOUNT_ADMIN.TRANSFER_EMAIL_HEADER'),
        inputs: [{
          name: 'email',
          type: 'email',
          placeholder: this.translate.instant('ACCOUNT_ADMIN.TRANSFER_EMAIL_PLACEHOLDER'),
        }],
        buttons: [
          {
            text: this.translate.instant('ACCOUNT_ADMIN.TRANSFER_CANCEL'),
            role: 'cancel',
            handler: () => finish(null),
          },
          {
            text: this.translate.instant('ACCOUNT_ADMIN.TRANSFER_CONTINUE'),
            handler: (data) => {
              const email = (data?.email || '').trim();
              if (!email) {
                this.showMessage(this.translate.instant('ACCOUNT_ADMIN.TRANSFER_EMAIL_REQUIRED'));
                return false;
              }
              finish(email);
            },
          },
        ],
      });
      alert.onDidDismiss().then(() => finish(null));
      await alert.present();
    });
  }

  private confirmTransfer(headerKey: string, message: string): Promise<boolean> {
    return new Promise(async (resolve) => {
      let settled = false;
      const finish = (value: boolean) => {
        if (!settled) {
          settled = true;
          resolve(value);
        }
      };
      const alert = await this.alertController.create({
        header: this.translate.instant(headerKey),
        message,
        buttons: [
          {
            text: this.translate.instant('ACCOUNT_ADMIN.TRANSFER_CANCEL'),
            role: 'cancel',
            handler: () => finish(false),
          },
          {
            text: this.translate.instant('ACCOUNT_ADMIN.TRANSFER_CONFIRM'),
            handler: () => finish(true),
          },
        ],
      });
      alert.onDidDismiss().then(() => finish(false));
      await alert.present();
    });
  }

  async removeKiln() {
    const alert = await this.alertController.create({
      header: this.translate.instant('MANAGE_NETWORK.DELETE_CONFIRMATION'),
      message: this.translate.instant('MANAGE_NETWORK.DELETE_KILN_CONFIRM_MESSAGE', { kilnName: this.selectedKiln.name }),
      buttons: [
        {
          text: this.translate.instant('MANAGE_NETWORK.YES_DELETE'),
          role: 'delete',
          cssClass: 'primary',
          handler: (data) => {
            this.accountService.unlinkNetworkDevice(this.accountInformation.id, {id: this.selectedKiln.id})
                .then((response) => {
                  this.userService.setCurrentAccount(this.accountInformation.general.external_id);
                  this.showMessage(this.translate.instant('MANAGE_NETWORK.DEVICE_REMOVED'));
                  this.getAccountDevices();
                  this.onDismiss();
                })
                .catch((response) => { this.upsertFailed(this.translate.instant('MANAGE_NETWORK.COULD_NOT_REMOVE_KILN')); })
          }
        }, {
          text: this.translate.instant('MANAGE_NETWORK.NO_CANCEL'),
          cssClass: 'secondary',
          role: 'cancel'
        }
      ]
    });

    await alert.present();
  }

  addNetworkDevice() {}

  setUpCanAdminister(data) {
    if(this.accountInformation && this.accountInformation.users && this.loginData) {
      let loggedInUserPermissions = this.accountInformation.users.find((u) => {
        return u.email ? (u.email === this.loginData.email) : (u.username === this.loginData.username)
      })

      this.canAdminister = loggedInUserPermissions ? (loggedInUserPermissions.type === 'admin') : false;
    }
  }

  getNdName(nd) {
    return nd.name ? nd.name : this.translate.instant('MANAGE_NETWORK.NAME_UNAVAILABLE_MAC', { mac: nd.mac_address || '' });
  }

  getPickerButtons() {
    return [
      {
        text: this.translate.instant('MANAGE_NETWORK.PICKER_CANCEL'),
        role: 'cancel',
      },
      {
        text: this.translate.instant('MANAGE_NETWORK.PICKER_CONFIRM'),
        handler: (selection) => {
          if(this.selected_tab.includes('kilns')) {
            this.selectKiln(selection.devices.value);
          } else {
            this.selecteNetworkDevice(selection.devices.value);
          }
        },
      },
    ];
  }

  async showPicker() {
    let options = []
    if(this.selected_tab.includes('kilns')) {
      options = this.kilns.map((k) => { return {text: k.name, value: k}})
    } else {
      options = this.network_devices.map((nd) => { return {text: nd.name, value: nd}})
    }        
    let picker = await this.pickerController.create({
      columns: [
      {
        name: 'devices',
        options: options
      }],
      buttons: this.getPickerButtons()
    });

    await picker.present();
  }

  goBack() {
    this.router.navigate(["/landing"]);
  }
}
